import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, of, Subscription } from 'rxjs';
import { Header } from '../../components/shared/header/header';
import { DropdownTable } from '../../components/shared/dropdown-table/dropdown-table';
import { LeadColumn, LeadRow, FilterConfig, ServerSidePagination, ViewButtonTracking } from '../../components/shared/leads-table/leads-table';
import { LeadsFilaService } from '../../services/leads-fila.service';
import { LeadsFilaItem, LeadsFilaRequest } from '../../interfaces/leads-fila.interface';
import { SelectedDnService } from '../../services/selected-dn.service';
import { AdminCampaignService } from '../../services/admin-campaign.service';
import { AdminCampaignApiItem } from '../../interfaces/admin-campaign.interface';

interface InsuranceGroupState {
  id: string;
  title: string;
  subtitle: string;
  isOpen: boolean;
  hasLoaded: boolean;
  loading: boolean;
  errorMessage: string;
  emptyMessage: string;
  tableData: LeadRow[];
  serverSidePagination?: ServerSidePagination;
  campaignCode?: string;
  filters: {
    searchText?: string;
    dateRange: { start: string; end: string };
    sortField: string;
    sortOrder: 'ASC' | 'DESC';
  };
}

@Component({
  selector: 'app-insurance-services',
  standalone: true,
  imports: [CommonModule, Header, DropdownTable],
  templateUrl: './insurance-services.html',
  styleUrls: ['./insurance-services.scss']
})
export class InsuranceServices implements OnInit, OnDestroy {
  private readonly tipoCampanha = 10;
  private dnSubscription?: Subscription;

  tableColumns: LeadColumn[] = [
    { key: 'nome', label: 'Nome', type: 'text' },
    { key: 'inicioContrato', label: 'Início Contrato', type: 'text' },
    { key: 'fimContrato', label: 'Fim Contrato', type: 'text' },
    { key: 'disponivelDesde', label: 'Disponível Desde', type: 'text' },
    { key: 'retornarEm', label: 'Retornar em', type: 'text' }
  ];

  filterConfig: FilterConfig = {
    searchFields: ['nome'],
    alphabeticalField: 'NM_NOME',
    dateField: 'disponivelDesde'
  };

  trackingConfig: ViewButtonTracking = {
    trackEvent: 'visualizar_seguros_servicos',
    trackCategory: 'seguros_servicos',
    trackLabel: 'view_seguro_servico'
  };

  groups: InsuranceGroupState[] = [];

  constructor(
    private leadsFilaService: LeadsFilaService,
    private selectedDnService: SelectedDnService,
    private adminCampaignService: AdminCampaignService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadGroups();

    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe((dn) => {
      if (!dn) {
        return;
      }

      this.groups = this.groups.map((group) => ({
        ...group,
        hasLoaded: false,
        tableData: [],
        serverSidePagination: undefined
      }));

      const firstOpenGroup = this.groups.find((group) => group.isOpen);
      if (firstOpenGroup) {
        this.loadGroupData(firstOpenGroup, 1, 20);
      }
    });
  }

  ngOnDestroy(): void {
    this.dnSubscription?.unsubscribe();
  }

  private loadGroups(): void {
    this.adminCampaignService.getCampaigns(undefined, undefined, this.tipoCampanha).pipe(
      catchError(() => of([] as AdminCampaignApiItem[]))
    ).subscribe((campaigns) => {
      const activeCampaigns = campaigns.filter((campaign) => campaign.flStatus === 'A' || campaign.ativo);

      if (activeCampaigns.length > 0) {
        this.groups = activeCampaigns.map((campaign, index) => this.createGroupState({
          id: `insurance-${campaign.idCampanha}`,
          title: campaign.dsCampanha,
          subtitle: `${this.formatDate(campaign.dtIni)} à ${this.formatDate(campaign.dtFim)} - Código ${campaign.cdCampanha}`,
          campaignCode: campaign.cdCampanha,
          isOpen: index === 0
        }));
      } else {
        this.groups = this.getFallbackGroups();
      }

      const firstOpenGroup = this.groups.find((group) => group.isOpen);
      if (firstOpenGroup) {
        this.loadGroupData(firstOpenGroup, 1, 20);
      }
    });
  }

  private createGroupState(config: {
    id: string;
    title: string;
    subtitle: string;
    campaignCode?: string;
    isOpen: boolean;
  }): InsuranceGroupState {
    return {
      id: config.id,
      title: config.title,
      subtitle: config.subtitle,
      campaignCode: config.campaignCode,
      isOpen: config.isOpen,
      hasLoaded: false,
      loading: false,
      errorMessage: '',
      emptyMessage: 'Nenhum lead encontrado para a campanha informada.',
      tableData: [],
      filters: {
        searchText: undefined,
        dateRange: { start: '', end: '' },
        sortField: 'DT_CONTR_FIM',
        sortOrder: 'DESC'
      }
    };
  }

  private getFallbackGroups(): InsuranceGroupState[] {
    return [
      this.createGroupState({
        id: 'legacy-insurance-1',
        title: 'Financiamentos sem revisão planejada 2024',
        subtitle: 'Clientes de financiamento que não contrataram revisão planejada',
        isOpen: true
      }),
      this.createGroupState({
        id: 'legacy-insurance-2',
        title: 'Financiamentos sem revisão planejada',
        subtitle: 'Clientes de financiamento que não contrataram revisão planejada',
        isOpen: false
      }),
      this.createGroupState({
        id: 'legacy-insurance-3',
        title: 'Seguros Vencidos',
        subtitle: 'Clientes com seguros vencidos para oportunidade de renovação',
        isOpen: false
      })
    ];
  }

  private loadGroupData(group: InsuranceGroupState, page: number = 1, pageSize: number = 10): void {
    group.loading = true;
    group.errorMessage = '';
    group.emptyMessage = 'Nenhum lead encontrado para a campanha informada.';

    const request: LeadsFilaRequest = {
      tipoCampanha: this.tipoCampanha,
      dn: this.selectedDnService.getSelectedDnAsNumber(),
      codigoCampanha: group.campaignCode,
      nome: group.filters.searchText,
      dataInicio: this.parseDate(group.filters.dateRange.start),
      dataFim: this.parseDate(group.filters.dateRange.end),
      page,
      pageSize,
      sortField: group.filters.sortField,
      sortOrder: group.filters.sortOrder
    };

    this.leadsFilaService.getLeadsFila(request).pipe(
      catchError((error) => {
        if (error.status === 401) {
          group.errorMessage = 'Não autorizado. Redirecionando...';
          setTimeout(() => this.router.navigate(['/login']), 1500);
        } else {
          group.errorMessage = 'Erro ao carregar dados de seguros e serviços.';
        }

        group.loading = false;
        group.tableData = [];
        return of(null);
      })
    ).subscribe((response) => {
      group.loading = false;
      group.hasLoaded = true;

      if (!response || !response.data || response.data.length === 0) {
        group.tableData = [];
        group.serverSidePagination = response?.pagination
          ? {
              totalRecords: response.pagination.totalRecords,
              page: response.pagination.page,
              pageSize: response.pagination.pageSize,
              from: response.pagination.from,
              to: response.pagination.to
            }
          : undefined;
        group.emptyMessage = response?.message || 'Nenhum lead encontrado para a campanha informada.';
        return;
      }

      group.tableData = response.data.map((item) => this.mapApiDataToTableData(item));
      group.serverSidePagination = {
        totalRecords: response.pagination.totalRecords,
        page: response.pagination.page,
        pageSize: response.pagination.pageSize,
        from: response.pagination.from,
        to: response.pagination.to
      };
    });
  }

  private mapApiDataToTableData(item: LeadsFilaItem): LeadRow {
    return {
      idLead: item.idLead,
      nome: this.normalizeValue(item.nomeCliente),
      inicioContrato: this.normalizeValue(item.inicioContrato),
      fimContrato: this.normalizeValue(item.terminoContrato),
      disponivelDesde: this.normalizeValue(item.disponivelDesde),
      retornarEm: this.normalizeValue(item.retornarEm)
    };
  }

  private normalizeValue(value: unknown): string {
    if (value === null || value === undefined || value === 'null' || value === 'undefined' || value === '') {
      return '-';
    }

    return String(value);
  }

  private parseDate(dateString: string): Date | undefined {
    if (!dateString || dateString.trim() === '') {
      return undefined;
    }

    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    }

    if (dateString.includes('-')) {
      return new Date(dateString);
    }

    return undefined;
  }

  private formatDate(value: string): string {
    if (!value) {
      return '-';
    }

    const rawDate = value.includes('T') ? value.split('T')[0] : value;

    if (!rawDate.includes('-')) {
      return rawDate;
    }

    const [year, month, day] = rawDate.split('-');
    if (!year || !month || !day) {
      return rawDate;
    }

    return `${day}/${month}/${year}`;
  }

  onGroupToggle(group: InsuranceGroupState, isOpen: boolean): void {
    group.isOpen = isOpen;

    if (isOpen && !group.hasLoaded) {
      this.loadGroupData(group, 1, 20);
    }
  }

  onPageChange(group: InsuranceGroupState, page: number): void {
    this.loadGroupData(group, page, group.serverSidePagination?.pageSize || 10);
  }

  onPageSizeChange(group: InsuranceGroupState, pageSize: number): void {
    this.loadGroupData(group, 1, pageSize);
  }

  onSearchChange(group: InsuranceGroupState, searchText: string): void {
    group.filters.searchText = searchText;
    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onDateRangeChange(group: InsuranceGroupState, dateRange: { start: string; end: string }): void {
    group.filters.dateRange = dateRange;
    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onSortChange(group: InsuranceGroupState, sort: { field: string; order: 'ASC' | 'DESC' }): void {
    group.filters.sortField = sort.field;
    group.filters.sortOrder = sort.order;
    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onClearAllFilters(group: InsuranceGroupState): void {
    group.filters.searchText = undefined;
    group.filters.dateRange = { start: '', end: '' };
    group.filters.sortField = 'DT_CONTR_FIM';
    group.filters.sortOrder = 'DESC';

    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onViewLead(lead: LeadRow): void {
    if (lead.idLead) {
      window.scrollTo(0, 0);
      this.router.navigate([`/leads/${lead.idLead}/detalhes`]);
    }
  }
}
